<template>
  <component
    :is="containerComponent"
    v-if="isAside || shortcutItems.length"
    ref="shortcutContainer"
    v-bind="containerAttributes"
    :class="containerClasses"
  >
    <span v-if="isMobile" slot="button-content">
      <AppIcon name="bookmark-filled" class="mr-1" />
      Favoritos
    </span>

    <div v-if="isAside" class="shortcuts-manager-header">
      <div class="shortcuts-manager-header-icon">
        <AppIcon icon="star-fill"></AppIcon>
      </div>
      <div>
        <div class="shortcuts-manager-heading">Favoritos</div>
        <div class="shortcuts-manager-description">Tus secciones más usadas, siempre a mano.</div>
      </div>
    </div>

    <div class="shortcuts-manager-content" @click.stop>
      <button type="button" class="shortcut-lock-bar" @click.prevent="toggleShortcutEditMode()">
        <AppIcon :icon="shortcutEditMode ? 'unlock-fill' : 'lock-fill'" class="shortcut-lock-icon"></AppIcon>
        <span>{{ shortcutEditMode ? 'Finalizar edición' : 'Organizar Favoritos' }}</span>
        <small>{{ shortcutEditMode ? 'Guardado automático' : 'Ordenar, renombrar o quitar' }}</small>
      </button>

      <button
        v-if="shortcutEditMode"
        type="button"
        class="shortcut-add-divider"
        @click.prevent="addShortcutDivider()"
      >
        <AppIcon icon="plus-circle" class="mr-1"></AppIcon>
        <span>Agregar separador</span>
      </button>

      <div v-if="!shortcutItems.length" class="shortcuts-manager-empty">
        <AppIcon icon="star" class="shortcuts-manager-empty-icon"></AppIcon>
        <strong>Todavía no tenés Favoritos</strong>
        <span>Marcá una pantalla con la estrella para encontrarla acá.</span>
      </div>

      <template v-for="(item, index) in shortcutItems">
        <div
          v-if="item.type === 'divider'"
          :key="'divider_' + index"
          class="shortcut-divider"
          :class="{ 'shortcut-divider-edit': shortcutEditMode }"
        >
          <div class="shortcut-divider-main">
            <span
              v-if="!shortcutEditMode || editingShortcutIndex !== index"
              class="shortcut-divider-title"
            >{{ item.title }}</span>
            <b-form-input
              v-else
              v-model="editingShortcutTitle"
              size="sm"
              class="shortcut-input"
              @click.stop
              @keydown.enter.prevent.stop="saveShortcutTitleByIndex(index)"
              @keydown.esc.prevent.stop="cancelShortcutEdit()"
            ></b-form-input>
          </div>
          <div v-if="shortcutEditMode" class="shortcut-actions">
            <button type="button" class="shortcut-action" :disabled="index === 0" title="Subir" @click.stop="moveShortcutByIndex(index, 'up')">
              <AppIcon icon="arrow-up"></AppIcon>
            </button>
            <button type="button" class="shortcut-action" :disabled="index === shortcutItems.length - 1" title="Bajar" @click.stop="moveShortcutByIndex(index, 'down')">
              <AppIcon icon="arrow-down"></AppIcon>
            </button>
            <button v-if="editingShortcutIndex !== index" type="button" class="shortcut-action" title="Renombrar" @click.stop="startShortcutEditByIndex(index, item)">
              <AppIcon icon="pencil-square"></AppIcon>
            </button>
            <button v-else type="button" class="shortcut-action shortcut-action-save" title="Guardar" @click.stop="saveShortcutTitleByIndex(index)">
              <AppIcon icon="check2-circle"></AppIcon>
            </button>
            <button v-if="editingShortcutIndex === index" type="button" class="shortcut-action" title="Cancelar" @click.stop="cancelShortcutEdit()">
              <AppIcon icon="x-circle"></AppIcon>
            </button>
            <button v-else type="button" class="shortcut-action shortcut-remove" title="Quitar separador" @click.stop="removeShortcutByIndex(index)">
              <AppIcon icon="trash"></AppIcon>
            </button>
          </div>
        </div>

        <div
          v-else
          :key="(item.fullPath || 'shortcut') + '_' + index"
          class="shortcut-item"
          :class="{ 'shortcut-item-compact': !shortcutEditMode, 'shortcut-item-editing': shortcutEditMode }"
          role="button"
          tabindex="0"
          @click="onShortcutItemClick(item)"
          @keydown.enter.prevent="onShortcutItemClick(item)"
        >
          <div class="shortcut-main">
            <span class="shortcut-item-icon-wrap">
              <AppIcon :name="getShortcutIcon(item)" class="shortcut-item-icon" />
            </span>
            <span
              v-if="!shortcutEditMode || editingShortcutIndex !== index"
              class="shortcut-title"
            >{{ item.title }}</span>
            <b-form-input
              v-else
              v-model="editingShortcutTitle"
              size="sm"
              class="shortcut-input"
              @click.stop
              @keydown.enter.prevent.stop="saveShortcutTitleByIndex(index)"
              @keydown.esc.prevent.stop="cancelShortcutEdit()"
            ></b-form-input>
          </div>
          <div v-if="shortcutEditMode" class="shortcut-actions">
            <button type="button" class="shortcut-action" :disabled="index === 0" title="Subir" @click.stop="moveShortcutByIndex(index, 'up')">
              <AppIcon icon="arrow-up"></AppIcon>
            </button>
            <button type="button" class="shortcut-action" :disabled="index === shortcutItems.length - 1" title="Bajar" @click.stop="moveShortcutByIndex(index, 'down')">
              <AppIcon icon="arrow-down"></AppIcon>
            </button>
            <button v-if="editingShortcutIndex !== index" type="button" class="shortcut-action" title="Renombrar" @click.stop="startShortcutEditByIndex(index, item)">
              <AppIcon icon="pencil-square"></AppIcon>
            </button>
            <button v-else type="button" class="shortcut-action shortcut-action-save" title="Guardar" @click.stop="saveShortcutTitleByIndex(index)">
              <AppIcon icon="check2-circle"></AppIcon>
            </button>
            <button v-if="editingShortcutIndex === index" type="button" class="shortcut-action" title="Cancelar" @click.stop="cancelShortcutEdit()">
              <AppIcon icon="x-circle"></AppIcon>
            </button>
            <button v-else type="button" class="shortcut-action shortcut-remove" title="Quitar" @click.stop="removeShortcutByIndex(index)">
              <AppIcon icon="trash"></AppIcon>
            </button>
          </div>
        </div>
      </template>
    </div>
  </component>
</template>

<script>
import shortcuts from '@/handler/shortcuts'
import { EventBus } from '@/handler/event-bus'

export default {
  name: 'ShortcutsManager',
  props: {
    presentation: {
      type: String,
      default: 'aside',
      validator: value => ['aside', 'mobile'].indexOf(value) !== -1,
    },
    navItems: {
      type: Array,
      default: () => [],
    },
    syncFromServer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      shortcutItems: [],
      shortcutEditMode: false,
      editingShortcutIndex: -1,
      editingShortcutTitle: '',
    }
  },
  computed: {
    isMobile() {
      return this.presentation === 'mobile'
    },
    isAside() {
      return this.presentation === 'aside'
    },
    containerComponent() {
      return this.isMobile ? 'b-dropdown' : 'div'
    },
    containerAttributes() {
      if (!this.isMobile) return {}

      return {
        right: true,
        size: 'sm',
        variant: 'outline-dark',
      }
    },
    containerClasses() {
      return {
        'shortcuts-manager': true,
        'shortcuts-manager--aside': this.isAside,
        'shortcuts-manager--mobile': this.isMobile,
        'd-lg-none': this.isMobile,
      }
    },
  },
  created() {
    EventBus.$on('Shortcuts:updated', this.handleShortcutsUpdated)
  },
  mounted() {
    this.loadShortcuts()
  },
  beforeDestroy() {
    EventBus.$off('Shortcuts:updated', this.handleShortcutsUpdated)
  },
  methods: {
    loadShortcuts() {
      this.shortcutItems = shortcuts.all()

      if (!this.syncFromServer) return

      shortcuts.syncFromServer().then(list => {
        if (list) {
          this.publishShortcuts(list)
        }
      })
    },
    handleShortcutsUpdated(list) {
      this.shortcutItems = Array.isArray(list) ? list : []
    },
    publishShortcuts(list) {
      this.shortcutItems = Array.isArray(list) ? list : []
      EventBus.$emit('Shortcuts:updated', this.shortcutItems)
    },
    onShortcutItemClick(item) {
      if (this.shortcutEditMode || !item || !item.fullPath) return

      this.$router.push(item.fullPath).catch(() => {})

      if (this.isMobile && this.$refs.shortcutContainer && this.$refs.shortcutContainer.hide) {
        this.$refs.shortcutContainer.hide()
      }
    },
    toggleShortcutEditMode() {
      this.shortcutEditMode = !this.shortcutEditMode
      if (!this.shortcutEditMode) {
        this.cancelShortcutEdit()
      }
    },
    addShortcutDivider() {
      this.publishShortcuts(shortcuts.addDivider('Grupo'))
    },
    startShortcutEditByIndex(index, item) {
      this.editingShortcutIndex = index
      this.editingShortcutTitle = item.title || ''
    },
    cancelShortcutEdit() {
      this.editingShortcutIndex = -1
      this.editingShortcutTitle = ''
    },
    saveShortcutTitleByIndex(index) {
      const value = (this.editingShortcutTitle || '').trim()
      if (!value) {
        this.$awn.warning('Ingrese un nombre')
        return
      }

      this.publishShortcuts(shortcuts.renameByIndex(index, value))
      this.cancelShortcutEdit()
    },
    moveShortcutByIndex(index, direction) {
      this.publishShortcuts(shortcuts.moveByIndex(index, direction))
    },
    removeShortcutByIndex(index) {
      if (this.editingShortcutIndex === index) {
        this.cancelShortcutEdit()
      }
      this.publishShortcuts(shortcuts.removeByIndex(index))
    },
    getShortcutIcon(item) {
      if (!item || !item.fullPath) return 'fa fa-bookmark'
      return this.findNavIconByPath(item.fullPath) || 'fa fa-bookmark'
    },
    findNavIconByPath(path) {
      if (!path || !this.navItems.length) return ''

      const clean = String(path).split('?')[0].split('#')[0].replace(/\/+$/, '')
      const findIcon = nodes => {
        for (let index = 0; index < nodes.length; index++) {
          const node = nodes[index]
          if (!node) continue

          if (node.url) {
            const nodeUrl = String(node.url).replace(/\/+$/, '')
            if (nodeUrl === clean || clean.startsWith(nodeUrl + '/')) {
              return node.icon || ''
            }
          }

          if (node.children && node.children.length) {
            const icon = findIcon(node.children)
            if (icon) return icon
          }
        }
        return ''
      }

      return findIcon(this.navItems)
    },
  },
}
</script>

<style>
.shortcuts-manager--mobile {
  width: 100%;
  padding: 0;
}
.shortcuts-manager--mobile > .btn {
  width: 100%;
  padding-top: 6px;
  padding-bottom: 6px;
  border-width: 2px;
  border-radius: 0;
  font-weight: 600;
}
.shortcuts-manager--mobile .dropdown-menu {
  width: 100%;
  min-width: 320px;
  padding: 0;
}
.shortcuts-manager--aside {
  min-height: 100%;
  background: var(--app-color-surface-subtle);
}
.shortcuts-manager-header {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 16px 14px 13px;
  border-bottom: 1px solid var(--app-color-border);
  background: var(--app-color-surface);
}
.shortcuts-manager-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 10px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  font-size: 16px;
}
.shortcuts-manager-heading {
  color: var(--app-color-text);
  font-size: 14px;
  font-weight: 700;
}
.shortcuts-manager-description {
  margin-top: 1px;
  color: var(--app-color-text-muted);
  font-size: 11px;
}
.shortcuts-manager-content {
  width: 100%;
  background: var(--app-color-surface);
}
.shortcut-lock-bar {
  display: grid;
  grid-template-columns: 17px 1fr;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid var(--app-color-border);
  background: var(--app-color-surface-subtle);
  color: var(--app-color-text-secondary);
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.shortcut-lock-bar:hover,
.shortcut-lock-bar:focus {
  outline: none;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-active);
}
.shortcut-lock-bar > span {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.35px;
  text-transform: uppercase;
}
.shortcut-lock-bar > small {
  grid-column: 2;
  color: var(--app-color-text-muted);
  font-size: 10px;
  line-height: 1.2;
}
.shortcut-lock-icon {
  margin-top: 1px;
  font-size: 12px;
}
.shortcut-add-divider {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 7px 12px;
  border: 0;
  border-bottom: 1px solid var(--app-color-border);
  background: var(--app-color-surface);
  color: var(--app-color-primary);
  font-size: 11px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}
.shortcut-add-divider:hover,
.shortcut-add-divider:focus {
  outline: none;
  background: var(--app-color-primary-soft);
}
.shortcuts-manager-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 35px 24px;
  color: var(--app-color-text-muted);
  text-align: center;
}
.shortcuts-manager-empty-icon {
  margin-bottom: 9px;
  color: var(--app-color-text-subtle);
  font-size: 25px;
}
.shortcuts-manager-empty strong {
  margin-bottom: 3px;
  color: var(--app-color-text-secondary);
  font-size: 12px;
}
.shortcuts-manager-empty span {
  max-width: 230px;
  font-size: 11px;
  line-height: 1.4;
}
.shortcut-divider {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 30px;
  padding: 5px 10px 5px 12px;
  border-top: 1px solid var(--app-color-border);
  border-bottom: 1px solid var(--app-color-border);
  background: var(--app-color-surface-muted);
}
.shortcut-divider-main {
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}
.shortcut-divider-title {
  color: var(--app-color-text-muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.55px;
  text-transform: uppercase;
}
.shortcut-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 7px;
  min-height: 39px;
  padding: 6px 9px 6px 12px;
  border-bottom: 1px solid var(--app-color-border);
  background: var(--app-color-surface);
  color: var(--app-color-text-secondary);
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease;
}
.shortcut-item:hover,
.shortcut-item:focus {
  outline: none;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-active);
}
.shortcut-item-editing {
  cursor: default;
}
.shortcut-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}
.shortcut-item-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  flex: 0 0 25px;
  border-radius: 7px;
  background: var(--app-color-surface-muted);
  color: var(--app-color-icon);
}
.shortcut-item-icon {
  width: 14px;
  text-align: center;
}
.shortcut-title {
  display: block;
  max-width: 100%;
  overflow: hidden;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shortcut-input {
  height: 25px;
  min-width: 70px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}
.shortcut-actions {
  display: inline-flex;
  align-items: center;
  gap: 1px;
}
.shortcut-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 23px;
  height: 23px;
  padding: 0;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: var(--app-color-text-muted);
  cursor: pointer;
  font-size: 12px;
}
.shortcut-action:hover,
.shortcut-action:focus {
  outline: none;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-active);
}
.shortcut-action:disabled {
  opacity: 0.3;
  cursor: default;
}
.shortcut-action-save {
  color: var(--app-color-success);
}
.shortcut-remove {
  color: var(--app-color-danger);
}
.shortcut-remove:hover,
.shortcut-remove:focus {
  background: var(--app-color-danger-soft);
  color: var(--app-color-danger-hover);
}
</style>
